import { useMarket } from "@app/_context";
import type { CustomProduct } from "@apptus/esales-api";
import { gtmAddToCartEvent } from "@features/tracking/gtm/addToCartEvent";
import { sendVoyadoCartChangeEvent } from "@features/tracking/voyado-tracking";
import { Cart } from "@repo/core/cart";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addToCart from "../services/addToCart";
import addToCartOptimistically from "../services/addToCartOptimistically";

export type AddCartItemInput = {
  product: CustomProduct;
  quantity: number;
}[];

export default function useAddCartItem() {
  const { market } = useMarket().state;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AddCartItemInput) => addToCart(input, market),
    onMutate: async (input: AddCartItemInput) => {
      await queryClient.cancelQueries({ queryKey: ["cart"] });
      await queryClient.cancelQueries({ queryKey: ["delivery"] });
      await queryClient.cancelQueries({ queryKey: ["payment"] });

      // Snapshot the previous cart
      const previousCart = queryClient.getQueryData<Cart>(["cart"]);

      // Optimistically update the cart
      queryClient.setQueryData<Cart | null>(["cart"], (currentCart) =>
        addToCartOptimistically(input, market, currentCart),
      );

      // Return the previous cart data to be used in the error callback
      return { previousCart };
    },
    onSuccess: (data: Cart, input: AddCartItemInput) => {
      void gtmAddToCartEvent({
        incomingItems: input.map((item) => ({
          partNo: item.product.key,
          quantity: item.quantity,
        })),
        cart: data ?? undefined,
      });

      void sendVoyadoCartChangeEvent({
        locale: market.contentfulLocale,
        cart: data ?? undefined,
      });
    },
    onError: (
      _error: Error,
      _input: AddCartItemInput,
      context: { previousCart: Cart | undefined } | undefined,
    ) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousCart) {
        queryClient.setQueryData<Cart>(["cart"], context.previousCart);
      }
    },
    onSettled: async () => {
      // Always refetch after error or success to ensure data is in sync
      void queryClient.invalidateQueries({ queryKey: ["cart"] });
      void queryClient.invalidateQueries({ queryKey: ["delivery"] });
      await queryClient.invalidateQueries({ queryKey: ["payment"] });
    },
  });
}
